import {Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {HelpersService} from '../../services/helpers.service';
import {AlertService} from '../../services/notify/alert.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {FileQueueService} from '../../services/file-queue.service';
import {ReplaySubject} from 'rxjs';
import {first} from 'rxjs/operators';
import {FileModel, PdfVersionsModel, SignersOrVisorsModel, VersionsModel} from '../../models/file.model';
import {NgxIndexedDBService} from 'ngx-indexed-db';
import {FileToolbarController} from './file-toolbar/file-toolbar.component';
import {DocumentsApiService, FileDataResponse} from '../../services/api/documents-api.service';
import {MemberService} from '../../services/member.service';
import {MemberModel} from '../../models/user.model';
import {ModalService} from '../../services/modal/modal.service';
import {DocumentHelpersService} from '../../services/helpers/document-helpers.service';
import {TranslateService} from '@ngx-translate/core';

export const fade = trigger('fade', [
    state('in', style({opacity: 1})),
    transition(':enter', [style({opacity: 0}), animate('300ms ease-out')]),
    transition(':leave', animate('150ms ease-out', style({opacity: 0}))),
]);

export const fullScreen = trigger('fullScreen', [
    state('in', style({opacity: 1})),
    transition(':enter', [style({opacity: 0}), animate('300ms ease-out')]),
    transition(':leave', animate('150ms ease-out', style({opacity: 0}))),
]);

@Component({
    selector: 'app-component-doc-view',
    templateUrl: './doc-view.component.html',
    styleUrls: ['./doc-view.component.scss'],
    animations: [fade, fullScreen],
})

export class DocViewComponent implements OnInit, OnDestroy {
    @Input() public fileUkey: string;
    @Input() public versionUkey: string;
    @Input() public fileData: FileDataResponse;
    @Input() public isImSign: boolean;
    /** выполнять редирект на выбранную версию */
    @Input() public redirectToVersion = false;
    @Input() public emitRefresh: EventEmitter<any> = new EventEmitter<any>();
    @Input() public emitReloadDoc: EventEmitter<string> = new EventEmitter<string>();
    @Input() public emitApproveDoc: EventEmitter<string> = new EventEmitter<string>();
    @Input() public isOpenModal = false;

    @Output() public emitLoadDocument: ReplaySubject<FileDataResponse> = new ReplaySubject<FileDataResponse>();
    @Output() public deleteEmit: EventEmitter<any> = new EventEmitter<any>();

    public currentMemberSigner: boolean;
    public isLoading = true;

    public toolbarController: FileToolbarController = new FileToolbarController();
    public isFullscreen = false;

    public fileModel: FileModel;
    public right: {
        is_read_all: boolean,
        is_read_approved_doc: boolean,
        is_edit_signer: boolean,
        is_edit_approver: boolean,
        is_edit_settings: boolean,
        is_edit_property: boolean,
        is_create_folder: boolean,
        is_upload_file: boolean,
        is_locked_collection: boolean,
        is_delete: boolean,
        is_read_my_doc: boolean,
        use_custom_rights: boolean,
    };
    public signers: SignersOrVisorsModel[];

    public currentMember: MemberModel;

    public currentVersion: VersionsModel | PdfVersionsModel;
    public isLoadingCurrentVersion = false;

    public isDiffVersion = false;

    public versions: /*PdfVersionsModel | VersionsModel*/any[];

    public virtualCurrentFileUrl: Uint8Array;

    isStopRefresh = false;

    allPages: number;
    lockSignButton = true;

    fileVersionError = false;
    fileVersionJob = false;

    isShowDropArea = false;
    isShowHoverDiv = false;
    zoomExt: string = '60'

    constructor(
        protected documentApi: DocumentsApiService,
        protected memberService: MemberService,
        public router: Router,
        public helper: HelpersService,
        protected documentHelper: DocumentHelpersService,
        public alert: AlertService,
        private fileQueueService: FileQueueService,
        private dbService: NgxIndexedDBService,
        private el: ElementRef,
        private modalService: ModalService,
        public translate: TranslateService
    ) {
        /* переключение фулскрина */
        this.toolbarController.$onFullscreen.subscribe(fs => this.isFullscreen = fs);

        /* команда на подпись */
        this.toolbarController.$onSing.subscribe(() => {
            setTimeout(() => {
                this.onLoadFileData();
            }, 1000);
        });

        /* команда на выбор версии */
        this.toolbarController.$onSelectVersion.subscribe((version: VersionsModel | PdfVersionsModel) => {
            this.virtualCurrentFileUrl = null;
            if (this.redirectToVersion) {
                void this.router.navigate(['/account', 'doc', 'nav', 'file', this.fileModel.key, version.key]);
            }
            this.currentVersion = version;

            this.fileVersionError = this.currentVersion.status === 'error';
            this.fileVersionJob = this.currentVersion.status === 'job';

            this.loadPdfFileBody();
        });
    }

    ngOnInit(): void {
        this.memberService.getCurrentMember().then(res => {
            this.currentMember = res;
            this.onInit();
        });
        this.emitReloadDoc.subscribe(ukey => {
            if (this.fileUkey !== ukey) {
                this.fileUkey = ukey;
                this.onInit();
            }
        });

        this.emitRefresh.subscribe(() => {
            this.onLoadFileData();
        });

        this.emitApproveDoc.subscribe(() => {
            this.onLoadFileDataApproved();
        });
    }

    onInit(): void {
        if (this.fileUkey) {
            this.onLoadFileData();
        } else if (this.fileData) {
            this.afterLoadFileDate(this.fileData);
        }
    }

    ngOnDestroy(): void {
        this.isStopRefresh = true;
        this.toolbarController.$onFullscreen.complete();
        this.toolbarController.$onDownload.complete();
        this.toolbarController.$onSing.complete();
        this.toolbarController.$onSelectVersion.complete();
    }

    onLoadFileData(): void {
        this.virtualCurrentFileUrl = null;
        this.isLoading = false;
        this.documentApi.getFileData(this.fileUkey).subscribe((data: FileDataResponse) => {
            this.afterLoadFileDate(data);
        });
    }

    onLoadFileDataApproved(): void {
        this.virtualCurrentFileUrl = null;
        this.documentApi.getFileData(this.fileUkey).subscribe((data: FileDataResponse) => {
            this.afterLoadFileDate(data);
            if (data.document.is_approved && (!data.document.pdf_version || data.document.pdf_version.status === 'ok')) {
                setTimeout(() => {
                    this.onLoadFileDataApproved();
                }, 3000);
            }
        });
    }

    afterLoadFileDate(data: FileDataResponse): void {
        this.isLoading = false;
        this.fileData = data;
        this.fileModel = data.document;
        this.fileUkey = data.document.key;
        this.right = data.right;
        this.signers = data.signers;
        const signer: SignersOrVisorsModel = this.signers.filter(s => s.user.guid === this.currentMember.guid)[0];
        if (signer !== undefined) {
            this.currentMemberSigner = true;
            this.isImSign = signer.sign;
        } else {
            this.currentMemberSigner = false;
            this.isImSign = true;
        }

        this.versions = this.getVersions();

        this.currentVersion = this.fileModel.pdf_version ? this.versions[0] : this.fileModel.latest_version;
        if (this.versionUkey) {
            const fl = this.fileModel.versions.filter(ver => ver.key === this.versionUkey);
            if (fl.length > 0) {
                this.currentVersion = fl[0];
            }
        } else if (!this.currentVersion && this.versions.length) {
            this.currentVersion = this.versions[0];
        }

        this.fileVersionError = this.currentVersion.status === 'error';
        this.fileVersionJob = this.currentVersion.status === 'job';

        this.emitLoadDocument.next(data);

        this.loadPdfFileBody();
    }

    getVersions(): /*PdfVersionsModel | VersionsModel*/any[] {
        let versions = [];
        if (this.fileModel.pdf_version) {
            versions = [Object.assign(new PdfVersionsModel(), this.fileModel.pdf_version)];
        }
        versions = versions.concat(this.fileModel.versions);
        return versions;
    }

    loadPdfFileBody(): void {
        // TODO обработчики на незагруженный или обрабатываемый демоном файл!!
        this.isLoadingCurrentVersion = false;

        this.getFileFromDb(this.currentVersion.key).then(result => {
            this.convertBlobToPdfFile(result);
            this.isLoadingCurrentVersion = true;
            this.isLoading = true;
        }).catch(error => {
            let req;
            if (error.message === 'No file' && this.currentVersion && this.currentVersion.status !== 'ok') {
                if (!this.isStopRefresh) {
                    return setTimeout(() => {
                        this.onLoadFileData();
                    }, 5000);
                } else {
                    return;
                }
            }
            if (this.currentVersion instanceof PdfVersionsModel) {
                req = this.documentApi.getIntermediateVersion(this.currentVersion.key);
            } else {
                req = this.documentApi.getFileBodyVersion(this.currentVersion.key, 'pdf', this.isDiffVersion);
            }

            req.pipe(first())
                .subscribe((data: any) => {
                    if (error.message === 'Other version') {
                        this.updateRowInDb(error.key, this.currentVersion.key, data);
                    } else {
                        this.addFileToDb(this.currentVersion.key, data);
                    }
                    this.convertBlobToPdfFile(data);
                    this.isLoadingCurrentVersion = true;
                    this.isLoading = true;
                }, () => {
                    this.isLoadingCurrentVersion = true;
                });
        });
    }

    convertBlobToPdfFile(data): void {
        const file = new Blob([data as BlobPart], {type: 'application/pdf'});
        new Response(file).arrayBuffer().then(buffer => {
            this.virtualCurrentFileUrl = new Uint8Array(buffer);
        });
    }

    updateFile(newVersionData): void {
        this.documentHelper.updateFileVersion(this.fileModel, newVersionData).subscribe(res => {
            this.checkStatusUploadingFile(res.file);
        });
    }

    checkStatusUploadingFile(file: FileModel): void {
        if (!this.fileUkey) {
            this.fileUkey = file.key;
        }
        const sub = this.fileQueueService.findNewTask().subscribe(res => {
            const newVersion = res.find(x => x.target_key === file.versions[0].key);
            if (newVersion.status !== 'ok') {
                setTimeout(() => {
                    this.checkStatusUploadingFile(file);
                }, 3000);
                sub.unsubscribe();
            } else {
                this.onLoadFileData();
            }
        });
    }

    getFileFromDb(version): Promise<any> {
        return new Promise((resolve, reject) => {
            this.dbService.getByIndex('files', 'version', version)
                .pipe(first())
                .subscribe({
                    next: (data: any) => {
                        if (data && data.version === version) {
                            resolve(data.file);
                        } else if (data && data.version !== version) {
                            reject({message: 'Other version', key: data.id});
                        } else {
                            reject({message: 'No file'});
                        }
                    }, error: () => {
                        reject({message: 'No file'});
                    },
                });
        });
    }

    addFileToDb(versionDoc, fil): void {
        this.dbService.add('files',
            {
                file_key: this.fileUkey,
                version: versionDoc,
                file: fil,
            }).pipe(first()).subscribe();
    }

    updateRowInDb(key, versionDoc, fil): void {
        this.dbService.update('files', {
            file_key: this.fileUkey,
            id: key,
            version: versionDoc,
            file: fil,
        });
    }

    checkPages(currentPage): void {
        if (this.allPages === currentPage) {
            this.lockSignButton = false;
        }
    }

    dragover(event: MouseEvent): void {
        this.isShowDropArea = true;
        event.preventDefault();
        event.stopPropagation();
    }

    dragenter(event: MouseEvent): void {
        this.isShowDropArea = true;
        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('document: mouseout', ['$event'])
    onDragEnter($event: any): void {
        $event.stopPropagation();
        $event.preventDefault();

        const from = $event.relatedTarget || $event.toElement;
        this.isShowHoverDiv = !from || from.nodeName === 'HTML';
    }

    @HostListener('document: dragleave', ['$event'])
    onDragLeave($event: DragEvent): void {
        if (!this.el.nativeElement.contains($event.target)) {
            $event.preventDefault();
            $event.stopPropagation();
            this.isShowDropArea = false;
        } else if (!$event.x && !$event.y) {
            this.isShowDropArea = false;
        }
    }

    drop(event: DragEvent): void {
        this.isShowDropArea = false;
        event.preventDefault();
        event.stopPropagation();
        const data = {
            file: event.dataTransfer.files[0],
            desc: '',
        };


        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
            modalBody: this.translate.instant('CONFIRM.TEXT.NEW_VERSION'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(res => {
            if (res) {
                this.updateFile(data);
            }
        }).catch(() => {
            return;
        });
    }
}
